// @ts-strict-ignore
import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild
} from '@angular/core';
import { LMContextService } from '@services/lm-context.service';
import { SohoAlertDirective } from 'ids-enterprise-ng';
import { Observable, Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged, first, takeUntil } from 'rxjs/operators';
import { LMLplEditorService } from './lm-lpl-editor.service';


@Component({
  selector: 'lm-lpl-editor',
  standalone: false,
  templateUrl: 'lm-lpl-editor.component.html',
  styleUrls: [ 'lm-lpl-editor.component.scss' ],
})

export class LMLplEditorComponent implements OnInit, AfterViewInit, OnDestroy {

  @ViewChild(SohoAlertDirective) alert: SohoAlertDirective;
  @ViewChild('editorContainer') _editorContainer: ElementRef;

  @Input() set validationOptions(validationOptions: LMEditorValidationOptions) {
    this._validationOptions = validationOptions;
    this.canValidateLpl = this.lmLplEditorService.canValidateLpl(validationOptions);
  }

  get validationOptions(): LMEditorValidationOptions {
    return this._validationOptions;
  }

  get lpl(): string {
    return this._lpl;
  }

  @Input() set lpl(lpl: string) {
    this._lpl = lpl;
    if (this.editor) {
      const model = this.editor.getModel() as monaco.editor.ITextModel;
      model.setValue(lpl);
    }
  };

  @Input() set readonly(readonly: boolean) {
    this._readonly = readonly;
    this.updateReadOnlyState();
  };

  get readonly(): boolean {
    return this._readonly;
  }

  @Output() lplChangedEvent = new EventEmitter<any>();

  private destroyer$ = new Subject<void>();
  public lplValidator$ = new Subject<string>();
  private editor: monaco.editor.IEditor;
  private _validationOptions: LMEditorValidationOptions;
  public canValidateLpl = false;
  private _readonly = false;
  private _lpl: string;
  public validatorTooltip: string;

  public monacoOptions: monaco.editor.IStandaloneEditorConstructionOptions = {
    theme: 'vs',
    automaticLayout: true,
    language: 'LPL',
    foldingStrategy: 'indentation',
    dragAndDrop: true,
    scrollBeyondLastLine: false
  };

  public secClassMonacoOptions: monaco.editor.IStandaloneEditorConstructionOptions = {
    theme: 'vs',
    automaticLayout: true,
    language: 'secclass',
    foldingStrategy: 'indentation',
    dragAndDrop: true,
    scrollBeyondLastLine: false
  };

  constructor(
    protected lmContextService: LMContextService,
    protected lmLplEditorService: LMLplEditorService) {
  }

  ngOnInit(): void {
    this.validatorTooltip = Soho.Locale.translate('LPL_EDITOR_VALIDATOR_TOOLTIP');
  }

  ngAfterViewInit(): void {
    this.initMonaco();

    this.lplValidator$.pipe(
      takeUntil(this.destroyer$),
      debounceTime(1250),
      distinctUntilChanged())
      .subscribe(value => {
        // if (!this.pauseChangeListeners) {  // no need to do if readonly
        this.validateLpl();
        // }
      });
  }

  ngOnDestroy(): void {
    this.destroyer$.next();
    this.destroyer$.complete();

    if (this.editor) {
      this.editor.dispose();
    }
  }

  private initMonaco() {
    if (!this.lmLplEditorService.isMonacoAvailable()) {
      this.lmLplEditorService.loadingMonacoFinished.pipe(first()).subscribe(() => {
        this.initMonaco();
      });
      return;
    }

    const options = this.validationOptions?.type === 'secClass' ? this.secClassMonacoOptions : this.monacoOptions;
    this.editor = monaco.editor.create(this._editorContainer.nativeElement, options);
    this.lmLplEditorService.syncMonacoThemeToSohoTheme();

    const model = this.editor.getModel() as monaco.editor.ITextModel;
    model.setValue(this.lpl );
    this.updateReadOnlyState();
    this.addMonacoListeners();
  }

  private updateReadOnlyState(): void {
    if (this.editor) {
      this.editor.updateOptions({
        readOnly: this.readonly,
        extraEditorClassName: this.readonly ? 'lm-readonly-editor' : ''
      });
    }
  }

  private addMonacoListeners(): void {
    const model = this.editor.getModel() as monaco.editor.ITextModel;
    model.onDidChangeContent((e) => {
      if (!this.readonly) {
        const lpl = model.getValue();
        this.lplChangedEvent.emit(lpl);
        this.lplValidator$.next(lpl);
      }
    });
  }

  private validateLpl(): void {
    if (this.readonly) {
      return;
    }

    if (this.validationOptions) {
      this._validateLpl().subscribe((response: LPLValidationResponse) => {
        this.updateLPLValidation(response);
      });
    }
  }

  private _validateLpl(): Observable<LPLValidationResponse> {
    const model = this.editor.getModel() as monaco.editor.ITextModel;
    const changedLPL = model.getValue();
    return this.lmLplEditorService.validateLpl(changedLPL, this.validationOptions);
  }

  private updateLPLValidation(lplValidationResponse?: LPLValidationResponse): void {
    const model = this.editor.getModel() as monaco.editor.ITextModel;
    this.lmLplEditorService.updateLPLValidation(model, lplValidationResponse, this.alert);
  }

  onMonacoClickEvent(id: string) {
    this.editor.focus();
    const actions: monaco.editor.IEditorAction[] = this.editor.getSupportedActions();
    const action = actions.find(item => item.id === id && item.isSupported());

    if (action) {
      action.run();
    }
  }

  onSecuritySnippetClickEvent(category: SecClassRuleType, snippet: SecClassType | SecClassObject) {
    this.editor.trigger('KeyBoard', 'type', {
      text: this.lmLplEditorService.getSecuritySnippet(category, snippet),
    });
  }

  onRepositoryHelperClickEvent(options: LMRepositoryHelperOptions) {
    options.rule = this.lmLplEditorService.parseSecuritySection(this.editor.getModel(), this.editor.getPosition());

    this.lmLplEditorService.openRepositoryHelperDialog(options).subscribe(response => {
      if (response) {
        this.editor.trigger('KeyBoard', 'type', {
          text: response.lpl,
        });
      }
    });
  }
}
